import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Eucalyptus } from "./Botanical";

const HERO_VIDEOS = ["/hero-video-1.mp4", "/hero-video-2.mp4"];

export const Hero = () => {
  const { scrollY } = useScroll();
  const textY = useTransform(scrollY, [0, 800], [0, -60]);
  const [active, setActive] = useState(2); // start with video 2
  const videoRefs = [useRef(null), useRef(null)];

  useEffect(() => {
    videoRefs.forEach((ref, i) => {
      const v = ref.current;
      if (!v) return;
      if (i === active - 1) {
        v.currentTime = 0;
        v.play().catch(() => {});
      } else {
        v.pause();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Watchdog: if the active video stalls (background tab throttle, play() rejection,
  // seek hang), resume it or advance to the next one so the loop never dies.
  useEffect(() => {
    const tick = () => {
      const v = videoRefs[active - 1].current;
      if (!v) return;
      if (v.ended) {
        setActive(active === 1 ? 2 : 1);
      } else if (v.paused) {
        v.play().catch(() => {});
      }
    };
    const id = setInterval(tick, 2000);
    const onVisible = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return (
    <header
      data-testid="wedding-hero-section"
      style={{ minHeight: "100svh" }}
      className="relative min-h-screen overflow-x-clip px-4 sm:px-8 lg:px-12 pt-14 sm:pt-[7rem] pb-24 sm:pb-20 flex items-center"
    >
      <motion.p
        dir="ltr"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.8 }}
        className="hidden sm:block absolute top-[3.25rem] sm:top-12 inset-x-0 z-20 font-script text-[1.26rem] sm:text-[1.68rem] tracking-[0.04em] text-[#3D362F] text-center [text-shadow:0_2px_24px_rgba(251,249,245,0.95)]"
        data-testid="hero-eyebrow"
      >
        You are invited to our wedding
      </motion.p>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.8 }}
        className="absolute inset-x-0 bottom-2"
        style={{ top: "calc(-1 * var(--ui-top, 0px))" }}
      >
        {HERO_VIDEOS.map((src, i) => (
          <video
            key={src}
            ref={videoRefs[i]}
            data-testid={i === 0 ? "hero-background-video" : `hero-background-video-${i + 1}`}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
            style={{ opacity: active === i + 1 ? 1 : 0, objectPosition: i === 0 ? "60% center" : "center 28%" }}
            src={src}
            muted
            playsInline
            preload="auto"
            onEnded={() => setActive(i === 0 ? 2 : 1)}
          />
        ))}
      </motion.div>
      <div
        className="absolute inset-x-0 bottom-0 bg-[#FBF9F5]/[0.55]"
        style={{ top: "calc(-1 * var(--ui-top, 0px))" }}
      />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#FBF9F5] to-transparent" />

      <Eucalyptus variant="spray" className="pointer-events-none absolute -top-2 -right-6 w-[clamp(calc(8.8rem*var(--bmin)),calc(0.16*var(--cw)),18rem)] z-[5] opacity-95" />


      <div className="pointer-events-none absolute top-36 right-7 sm:top-[19rem] sm:right-24 min-[1232px]:right-16 z-[5] flex flex-col items-center">
        <span className="w-1.5 h-1.5 rounded-full bg-[#C29B38]" />
        <span className="w-px h-[20vh] sm:h-[32vh] bg-gradient-to-b from-[#7A8B7B]/70 to-transparent" />
      </div>
      <div className="pointer-events-none absolute bottom-36 left-7 sm:bottom-[13rem] sm:left-16 min-[1232px]:left-12 z-[5] flex flex-col items-center">
        <span className="w-px h-[20vh] sm:h-[32vh] bg-gradient-to-b from-transparent to-[#7A8B7B]/70" />
        <span className="w-1.5 h-1.5 rounded-full bg-[#C29B38]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-0 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-12 relative z-20 text-center lg:text-right [text-shadow:0_2px_24px_rgba(251,249,245,0.95)]">
          <p
            dir="ltr"
            data-testid="hero-eyebrow-mobile"
            className="sm:hidden mb-5 -translate-y-6 px-6 font-script text-[1.26rem] leading-snug tracking-[0.04em] text-[#3D362F] text-center"
          >
            You are invited to our wedding
          </p>
          <h1 dir="ltr" className="font-display text-[#1A1612] leading-[1.25] text-center lg:text-left">
            <span className="block overflow-hidden text-[4.05rem] sm:text-[5.4rem] lg:text-[6.4rem] pt-4 sm:pt-6 pb-3 sm:pb-5">
              Parmiss
            </span>
            <span className="block overflow-hidden text-[2.02rem] sm:text-[2.7rem] lg:text-[2.4rem] text-[#C29B38] pt-1 pb-3 sm:pb-4 lg:ps-20">
              &amp;
            </span>
            <span className="block overflow-hidden text-[4.05rem] sm:text-[5.4rem] lg:text-[6.4rem] pt-4 sm:pt-6 pb-3 sm:pb-5">
              Samie
            </span>
          </h1>

          <div
            className="mt-6 flex items-center justify-center lg:justify-start gap-3 sm:gap-5"
            data-testid="hero-farsi-names"
          >
            <img src="/name-parmiss.png" alt="پارمیس" className="h-[3.3rem] sm:h-[4.7rem] lg:h-[5.5rem] w-auto" />
            <img src="/name-va.png" alt="و" className="h-5 sm:h-7 lg:h-8 w-auto -mt-2" />
            <img src="/name-samie.png" alt="سمیع" className="h-10 sm:h-14 lg:h-[4.5rem] w-auto mt-3 lg:mt-5" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-sm sm:text-base text-[#524A42]"
            data-testid="hero-date-venue"
          >
            <span>یکشنبه</span>
            <span className="text-[#C29B38]">·</span>
            <span className="border-b border-[#C29B38] pb-1">۱۲ مهر ۱۴۰۵</span>
            <span className="text-[#C29B38]">·</span>
            <span>۴ اکتبر ۲۰۲۶</span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="mt-6 text-[#3D362F] text-sm sm:text-base font-light"
          >
            صمیمانه از شما دعوت می‌کنیم در جشن پیوندمان همراه ما باشید
          </motion.p>
        </motion.div>

      </div>
    </header>
  );
};
